import React, {FC, ReactNode} from 'react';

import {Plural, Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useShareDraftOfflinePermission} from '../../../../../srcCommon/permissions/useOfflinePermission';
import {InboxModel} from '../../../../models/api/inboxModel';
import {DraftMessageModel} from '../../../../models/api/messageModel';
import {OkCancelDialog} from '../../../common/dialogs/okCancelDialog';
import {PermissionPopoverCoordinator} from '../../../common/popovers/permissionPopoverCoordinator';
import {ShareDropdownContainer} from './shareDropdownContainer';

/*
 * Props.
 */

export type ShareMessage = (trackerIds?: ReadonlyArray<number | string>, commentText?: string) => void;

export interface ShareButtonCoordinatorProps {
  message: DraftMessageModel;
  isMessageShared: boolean;
  shareMessage: ShareMessage;
  participantIds: ReadonlyArray<number>;
  conversationTeamInboxes: ReadonlyArray<InboxModel>;
  shouldInviteTeammates: boolean;
  renderButton: (
    isShared: boolean,
    participantIds: ReadonlyArray<number>,
    isModalOpen: boolean,
    isDisabled?: boolean,
  ) => ReactNode;
}

/*
 * Component.
 */

export const ShareButtonCoordinator: FC<ShareButtonCoordinatorProps> = (props) => {
  const offlinePermission = useShareDraftOfflinePermission();

  return (
    <PermissionPopoverCoordinator
      offlinePermission={offlinePermission}
      renderButton={(isModalOpen) =>
        props.renderButton(props.isMessageShared, props.participantIds, isModalOpen)
      }
      renderDisabledButton={() =>
        props.renderButton(props.isMessageShared, props.participantIds, false, true)
      }
      renderModal={(modalProps) => renderShareDropdown(modalProps, props)}
    />
  );
};

/*
 * Dialog.
 */

function renderShareDropdown(popoverProps: PopoverTransferProps, ownProps: ShareButtonCoordinatorProps) {
  if (ownProps.isMessageShared) {
    return null;
  }

  const onShareButtonClick = () => {
    popoverProps.onRequestClose();
    ownProps.shareMessage();
  };

  return ownProps.shouldInviteTeammates ? (
    <ShareDropdownContainer
      existingTrackers={[]}
      {...popoverProps}
      message={ownProps.message}
      onShareMessage={ownProps.shareMessage}
      onCancelClick={popoverProps.onRequestClose}
      onRequestClose={popoverProps.onRequestClose}
    />
  ) : (
    <OkCancelDialog
      {...popoverProps}
      title={<Trans id="composer-share-title">Shared draft</Trans>}
      okButtonLabel={<Trans id="composer-share-ok">Share</Trans>}
      onOkClick={onShareButtonClick}
      width={470}
    >
      {buildAccessText(ownProps.conversationTeamInboxes)}{' '}
      <Trans id="shared-draft-feedback">Notify a teammate by @mentioning them in a comment below.</Trans>
    </OkCancelDialog>
  );
}

function buildAccessText(teamInboxes: ReadonlyArray<InboxModel>) {
  const singleInboxName = teamInboxes[0]?.name || '';
  return (
    <Plural
      id="warning-share-draft"
      value={teamInboxes.length}
      _0="This draft will be visible to all participants in this conversation."
      one={
        <>
          This draft will be visible to all teammates in the <strong>{singleInboxName}</strong> team inbox.
        </>
      }
      other={<>This draft will be visible to all teammates in {teamInboxes.length} team inboxes.</>}
    />
  );
}
